/**
 * StatTile — одно число с подписью, тоном и необязательным пояснением.
 *
 * Живёт в двух положениях. Отдельно — сам себе карточка: фон, радиус,
 * padding, пояснение в углу. Внутри `SplitPanel` — без собственной
 * оболочки (`bare`): поверхность и отступы уже дала панель, а вторая
 * подложка поверх первой читается как вложенный блок, которым тайл не
 * является.
 *
 * В `bare` пояснение стоит в строке подписи, а не в углу: угол принадлежит
 * группе целиком, и значок там указывал бы не на ту ячейку.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {MEASURE} from '../helpers';
import type {PaletteTone} from '../content';
import {HelpHint} from './HelpHint';
import {Metric, type MetricSize} from './Metric';
import {ReportCard} from './ReportCard';

export interface StatTileProps {
  label: string;
  value: string;
  /** Единица, набирается мельче и приглушённо рядом с числом. */
  unit?: string;
  /** Пояснение к показателю. */
  hint?: string;
  /** Тон числа. Без него — нейтральный. */
  tone?: PaletteTone;
  /** Строка под числом. */
  caption?: string;
  /** Без собственной карточки — когда оболочку даёт родитель. */
  bare?: boolean;
  /**
   * Кегль числа. По умолчанию `m` (24) — тайл обычно стоит в ряду себе
   * подобных. `l` (32) для случая, когда пара тайлов и есть итог раздела.
   */
  size?: MetricSize;
}

const Tile = styled(ReportCard)({
  flex: '1 1 200px',
  minWidth: 180,
});

const Label = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE,
});

/** В карточке подпись обходит угловой значок пояснения. */
const LabelWithCorner = styled(Label)({
  paddingRight: spacingPx('l'),
});

export function StatTile({
  label,
  value,
  unit,
  hint,
  tone,
  caption,
  bare,
  size = 'm',
}: StatTileProps) {
  const metric = <Metric value={value} unit={unit} caption={caption} tone={tone} size={size} />;

  if (bare) {
    return (
      <Stack gap='m'>
        <Row gap='xs' alignY='center'>
          <Label>{label}</Label>
          {hint && <HelpHint label={label}>{hint}</HelpHint>}
        </Row>
        {metric}
      </Stack>
    );
  }

  return (
    <Tile hint={hint} hintLabel={label}>
      <Stack gap='m'>
        <LabelWithCorner>{label}</LabelWithCorner>
        {metric}
      </Stack>
    </Tile>
  );
}
